import { Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import { CommonModule, Location, UpperCasePipe } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { DATE_FORMAT } from 'src/app/shared/constants';
import {
    DxButtonModule,
    DxDateBoxModule,
    DxFormModule,
    DxLoadPanelModule,
    DxRadioGroupModule,
    DxScrollViewModule,
    DxSelectBoxModule,
    DxTagBoxModule,
    DxTextAreaModule,
    DxTextBoxModule,
    DxToolbarModule,
    DxValidationGroupComponent,
    DxValidationGroupModule,
    DxValidatorModule,
} from 'devextreme-angular';
import { DxScrollViewComponent } from 'devextreme-angular/ui/scroll-view';
import {
    AuthService,
    PermissionType,
    AccessType,
    DataSources,
    InvoicingClient,
    InvoicePurchaseOrderDto,
    InvoicePurchaseOrderStatus,
    CreateInvoicePurchaseOrderCommand,
    UpdateInvoicePurchaseOrderCommand,
    InvoicePurchaseOrderAction,
    CurrencyCode,
    FinancialDocumentItemDto,
    VendorInfoDto,
    BillToInfoDto,
    RecurrenceRuleDto,
} from '@services';
import EnumUtils from '../../../../../shared/enum-utils';
import notify from 'devextreme/ui/notify';
import DataSource from 'devextreme/data/data_source';
import { FileUtils } from 'src/app/shared/file-utils';
import { DxCheckBoxModule } from 'devextreme-angular/ui/check-box';
import { InvoicePurchaseOrderDetailsToolbarComponent } from './components/toolbar/invoice-purchase-order-details-toolbar.component';
import { FormatPhonePipe } from '../../../../../pipes/format-phone.pipe';
import { InvoicePurchaseOrderDetailsRecurrenceComponent } from './components/recurrence/invoice-purchase-order-details-recurrence';
import { InvoiceDetailsItemsComponent } from '../../../components/invoice-details-items.component/invoice-details-items.component';
import { CapitalCasePipe } from 'src/app/pipes/capital-case.pipe';
import { NotesComponent, NoteType } from 'src/app/components/library/notes/notes.component';

@Component({
    selector: 'app-invoice-purchase-order-details',
    imports: [
        CommonModule,
        DxToolbarModule,
        DxButtonModule,
        DxLoadPanelModule,
        DxValidationGroupModule,
        DxTextBoxModule,
        DxValidatorModule,
        DxSelectBoxModule,
        DxTagBoxModule,
        DxDateBoxModule,
        DxScrollViewModule,
        DxFormModule,
        DxRadioGroupModule,
        DxTextAreaModule,
        DxCheckBoxModule,
        FormatPhonePipe,
        InvoiceDetailsItemsComponent,
        InvoicePurchaseOrderDetailsToolbarComponent,
        InvoicePurchaseOrderDetailsRecurrenceComponent,
        RouterLink,
        CapitalCasePipe,
        NotesComponent,
    ],
    providers: [UpperCasePipe],
    templateUrl: './invoice-purchase-order-details.component.html',
})
export class InvoicePurchaseOrderDetailsComponent implements OnInit {
    @Input() number: string | null = null;
    @ViewChild('validationGroup', { static: true }) validationGroup: DxValidationGroupComponent;
    @ViewChild(DxScrollViewComponent, { static: false }) scrollView?: DxScrollViewComponent;
    @ViewChild(NotesComponent) notesComponent: NotesComponent;

    private route = inject(ActivatedRoute);
    private location = inject(Location);
    private router = inject(Router);
    private auth = inject(AuthService);
    private dataSources = inject(DataSources);
    private invoicingClient = inject(InvoicingClient);
    private upperCasePipe = inject(UpperCasePipe);
    
    protected readonly PermissionType = PermissionType;
    protected readonly NoteType = NoteType;

    currencyOptions = EnumUtils.getEnumOptions(CurrencyCode, (v) =>
        this.upperCasePipe.transform(v),
    );

    dateFormat = DATE_FORMAT;
    minDueDate = new Date();
    serviceTypeDataSource = this.dataSources.availableServiceTypeOptions();
    vendorDataSource = this.dataSources.activeVendorOptions();
    billToDataSource: DataSource = this.buildBillToDataSource();
    isLoading: boolean = false;
    isSubmitting: boolean = false;
    isExporting: boolean = false;
    isReadOnly = false;
    invoicePurchaseOrder: InvoicePurchaseOrderDto = new InvoicePurchaseOrderDto();
    vendor: VendorInfoDto | null = null;
    billTo: BillToInfoDto | null = null;
    pendingNoteAction: InvoicePurchaseOrderAction | null = null;
    recurrenceRule?: RecurrenceRuleDto;
    actionsRequiringNote: InvoicePurchaseOrderAction[] = [
        InvoicePurchaseOrderAction.Cancel,
    ];

    // When viewing, feed the Bill To box the stored value so it always renders, even if the current
    // user's site scope would exclude it. Editing uses the scoped remote options.
    private buildBillToDataSource(): DataSource {
        if (this.isReadOnly) {
            return new DataSource({
                key: 'id',
                store: this.invoicePurchaseOrder?.billToClientId
                    ? [{ id: this.invoicePurchaseOrder.billToClientId, name: this.invoicePurchaseOrder.billToName }]
                    : [],
            });
        }

        return new DataSource({
            store: this.dataSources.billToOptions(PermissionType.InvoicePurchaseOrders),
            paginate: true,
        });
    }

    ngOnInit(): void {
        this.number = this.route.snapshot.paramMap.get('number');
        this.recurrenceRule = undefined;

        if (this.number) {
            this.loadInvoicePurchaseOrder();
        } else {
            this.initializeNewInvoicePurchaseOrder();
        }
    }

    private initializeNewInvoicePurchaseOrder() {
        this.invoicePurchaseOrder = new InvoicePurchaseOrderDto();
        this.invoicePurchaseOrder.items = [
            FinancialDocumentItemDto.fromJS({
                quantity: 1,
                description: '',
                unitPrice: 0,
                taxRate: 0,
            }),
        ];
        this.invoicePurchaseOrder.subTotal = 0;
        this.invoicePurchaseOrder.taxAmount = 0;
        this.invoicePurchaseOrder.totalAmount = 0;

        this.isReadOnly = !this.auth.checkPermission(PermissionType.InvoicePurchaseOrderDetails, AccessType.Edit);
        this.billToDataSource = this.buildBillToDataSource();
    }

    private loadInvoicePurchaseOrder() {
        this.isLoading = true;
        this.invoicingClient.getInvoicePurchaseOrder(this.number!).subscribe({
            next: async (data) => {
                this.invoicePurchaseOrder = data;
                this.recurrenceRule = data.recurrenceRule;
                this.isLoading = false;
                this.vendor = {
                    serviceTypeId: data.serviceTypeId,
                } as VendorInfoDto;
                this.updateVendorInfo(false);
                this.updateBillToInfo();
                this.onServiceTypeChanged();

                const canEdit = this.auth.checkPermission(PermissionType.InvoicePurchaseOrderDetails, AccessType.Manage)
                    || (this.invoicePurchaseOrder.status === InvoicePurchaseOrderStatus.Draft 
                        && this.auth.checkPermission(PermissionType.InvoicePurchaseOrderDetails, AccessType.Edit));
                
                this.isReadOnly = !canEdit;
                this.billToDataSource = this.buildBillToDataSource();
            },
            error: () => {
                notify('Failed to load invoice purchase order', 'error', 3000);
                this.isLoading = false;
                this.location.back();
            },
        });
    }

    get canViewNotes(){
        return this.auth.checkPermission(PermissionType.InvoicePurchaseOrderNotes, AccessType.ViewOnly);
    }

    get canCreateNotes(){
        return this.auth.checkPermission(PermissionType.InvoicePurchaseOrderNotes, AccessType.Manage);
    }

    get canCreateInternalNotes(){
        return this.auth.checkPermission(PermissionType.InvoicePurchaseOrderInternalNotes, AccessType.Manage);
    }

    protected getBadgeClass(
        status: InvoicePurchaseOrderStatus | undefined,
    ): string {
        return EnumUtils.getInvoicePurchaseOrderStatusBadgeClass(status);
    }

    protected updateVendorInfo(applyGlCodeDefaults: boolean) {
        if (this.invoicePurchaseOrder.vendorId) {
            this.invoicingClient
                .getVendorInfo(this.invoicePurchaseOrder.vendorId)
                .subscribe({
                    next: (vendor) => {
                        this.vendor = vendor;

                        if (this.invoicePurchaseOrder.serviceTypeId === undefined) {
                            this.invoicePurchaseOrder.serviceTypeId = vendor.serviceTypeId;
                        }

                        if (applyGlCodeDefaults) {
                            this.invoicePurchaseOrder.items?.forEach((item) => {
                                item.glCodeId = vendor.glCodeId;
                                item.glCodeName = vendor.glCodeName;
                                item.siteId = vendor.glCodeSiteId;
                            });
                        }
                    },
                    error: () => {
                        notify('Failed to load vendor details', 'error', 3000);
                        this.vendor = null;
                    },
                });
        } else {
            this.vendor = null;
        }
    }

    protected updateBillToInfo() {
        if (this.invoicePurchaseOrder.billToClientId) {
            this.invoicingClient
                .getBillToInfo(this.invoicePurchaseOrder.billToClientId)
                .subscribe({
                    next: (billTo) => {
                        this.billTo = billTo;
                    },
                    error: () => {
                        this.billTo = null;
                    },
                });
        } else {
            this.billTo = null;
        }
    }

    protected onServiceTypeChanged() {
        this.vendorDataSource = this.dataSources.activeVendorOptions(
            this.invoicePurchaseOrder.serviceTypeId,
        );
        if (
            this.vendor?.serviceTypeId !==
            this.invoicePurchaseOrder.serviceTypeId
        ) {
            this.invoicePurchaseOrder.vendorId = undefined;
        }
    }

    async onDateChanged() {
        if (!this.invoicePurchaseOrder.issueDate) return;

        this.minDueDate = this.invoicePurchaseOrder.issueDate;
        
        this.updateCurrency();
    }

    updateCurrency() {
        if (this.invoicePurchaseOrder.currency == CurrencyCode.USD && this.invoicePurchaseOrder.issueDate) {
            const conversionDate = new Date(this.invoicePurchaseOrder.issueDate);
            conversionDate.setMonth(conversionDate.getMonth() - 1);

            this.invoicingClient
                .getConversionRate(conversionDate)
                .subscribe({
                    next: (conversionRate) => {
                        this.invoicePurchaseOrder.conversionRate = conversionRate;
                    },
                    error: () => {
                        notify(
                            'Failed to load conversion rate',
                            'error',
                            3000,
                        );
                        this.invoicePurchaseOrder.conversionRate =
                            undefined;
                    },
                });
        } else {
            this.invoicePurchaseOrder.conversionRate = undefined;
        }
    }

    get isRecurrenceVisible() {
        return this.number == undefined || this.invoicePurchaseOrder.status == InvoicePurchaseOrderStatus.Draft;
    }

    onSaveClick(action: InvoicePurchaseOrderAction) {     
        if(this.actionsRequiringNote.includes(action))
        {
            this.pendingNoteAction = action;
            this.notesComponent.openCreatePopup();

        } else{
            this.save(action);
        }
    }

    onNoteSaved() {
        if (!this.pendingNoteAction)
        {
            return;
        }

        const action = this.pendingNoteAction;
        this.pendingNoteAction = null;
        this.save(action);
    }

    onNoteCanceled() {
        this.pendingNoteAction = null;
    }

    save(action: InvoicePurchaseOrderAction) {
        const result = this.validationGroup.instance.validate();

        if (!result.isValid) {
            notify(
                {
                    message: 'Please fix validation errors',
                    position: 'bottom center',
                },
                'error',
                3000,
            );
            return;
        }

        this.isSubmitting = true;

        const command = this.number
            ? new UpdateInvoicePurchaseOrderCommand({
                  ...this.invoicePurchaseOrder,
                  number: this.number,
                  action: action,
                  recurrenceRule: this.recurrenceRule,
              })
            : new CreateInvoicePurchaseOrderCommand({
                  ...this.invoicePurchaseOrder,
                  action: action,
                  recurrenceRule: this.recurrenceRule,
              });

        const request$ = this.number
            ? this.invoicingClient.updateInvoicePurchaseOrder(command)
            : this.invoicingClient.createInvoicePurchaseOrder(command);

        request$.subscribe({
            next: async (id) => {
                if (this.number === null && this.notesComponent) {
                    await this.notesComponent.saveQueuedNotes(id);
                }

                notify(
                    this.number
                        ? 'PO updated successfully'
                        : 'PO created successfully',
                    'success',
                    3000,
                );
                this.isSubmitting = false;
                if (this.number) {
                    this.loadInvoicePurchaseOrder();
                } else {
                    await this.router.navigate(
                        ['/invoicing/purchase-orders/details', id],
                        {
                            replaceUrl: true,
                        },
                    );
                }
            },            
            error: (errors: Array<any>) => {
                const msg = errors?.[0]?.message ||
                    (this.number
                        ? 'Failed to update purchase order'
                        : 'Failed to create purchase order');
                notify(msg, 'error', 3000);
                this.isSubmitting = false;
            },
        });
    }

    exportPdf() {
        this.isExporting = true;
        this.invoicingClient.exportInvoicePurchaseOrderPdf(this.number!).subscribe({
            next: (file) => {
                FileUtils.downloadFile(file);
                this.isExporting = false;
            },
            error: () => {
                notify('Failed to export PO', 'error', 3000);
                this.isExporting = false;
            },
        });
    }

    cancel() {
        this.location.back();
    }
}
